/**
 * Foundation Viewer
 * Interactive display of all design tokens (colors, spacing, typography, etc.)
 */

import React, { useState } from "react";
import { Box, Grid, SimpleGrid, Card, CardBody, Button, Code, useToast, useColorMode } from "@chakra-ui/react";
import {
  SpacingToken,
  TextColorToken,
  SurfaceColorToken,
  BorderColorToken,
  InteractiveColorToken,
  ShadowToken,
  resolveSpacing,
  resolveShadow,
} from "design-system/foundation";
import { useColorModeValue } from "design-system/hooks/useColorModeValue";
import CDS from "design-system";

// Helper component to show color tokens reactively
function ReactiveColorBox({
  colorToken,
}: {
  colorToken: SurfaceColorToken | TextColorToken | BorderColorToken | InteractiveColorToken;
}) {
  const resolvedColor = useColorModeValue(colorToken);
  return (
    <Box
      h="40px"
      w="full"
      borderRadius="md"
      border="1px solid"
      borderColor="gray.200"
      style={{ backgroundColor: resolvedColor }}
    />
  );
}

// Helper component for primitive color squares
function ReactivePrimitiveColorBox({ hue, scale, onCopy }: { hue: string; scale: number; onCopy: () => void }) {
  const colorToken = (CDS.Color.primitive as any)[hue](scale);
  const resolvedColor = useColorModeValue(colorToken);

  return (
    <Box position="relative" cursor="pointer" onClick={onCopy}>
      <Box
        w="40px"
        h="40px"
        borderRadius="md"
        border="1px solid"
        borderColor="gray.200"
        style={{ backgroundColor: resolvedColor }}
        _hover={{ transform: "scale(1.1)" }}
        transition="transform 0.1s"
      />
      <CDS.Text
        variant={CDS.Text.variant.caption}
        color={CDS.Color.text.secondary}
        style={{ textAlign: "center", marginTop: "4px" }}
      >
        {scale}
      </CDS.Text>
    </Box>
  );
}

// Helper component for shadow display
function ReactiveShadowBox({ shadowToken }: { shadowToken: ShadowToken }) {
  const { colorMode } = useColorMode();
  const isDarkMode = colorMode === "dark";
  const resolvedShadow = resolveShadow(shadowToken, isDarkMode);

  return (
    <Box
      h="60px"
      w="full"
      bg={isDarkMode ? "gray.800" : "white"}
      borderRadius="md"
      display="flex"
      alignItems="center"
      justifyContent="center"
      position="relative"
    >
      <Box
        w="40px"
        h="40px"
        bg={isDarkMode ? "gray.600" : "gray.100"}
        borderRadius="md"
        style={{ boxShadow: resolvedShadow }}
      />
    </Box>
  );
}

// Helper component for radius display

interface TokenDisplayProps {
  name: string;
  value:
    | string
    | TextColorToken
    | SurfaceColorToken
    | BorderColorToken
    | InteractiveColorToken
    | ShadowToken
    | RadiusToken;
  token: string;
  type: "color" | "spacing" | "typography" | "shadow" | "radius";
  example?: React.ReactNode;
}

function TokenDisplay({ name, value, token, type, example }: TokenDisplayProps) {
  const toast = useToast();

  // Get color mode for shadow resolution
  const { colorMode } = useColorMode();
  const isDarkMode = colorMode === "dark";

  // Resolve color tokens reactively
  const isColorToken = type === "color" && typeof value === "object" && "_semantic" in value;
  const colorValue = useColorModeValue(
    isColorToken ? (value as TextColorToken | SurfaceColorToken | BorderColorToken | InteractiveColorToken) : undefined,
  );

  // Resolve different token types for display
  const resolvedValue = React.useMemo(() => {
    if (type === "color" && colorValue) {
      return colorValue;
    }
    if (type === "shadow" && typeof value === "object" && "_lightValue" in value) {
      return resolveShadow(value as ShadowToken, isDarkMode);
    }
    if (type === "radius" && typeof value === "object" && "_spacingToken" in value) {
      return resolveRadius(value as RadiusToken);
    }
    return typeof value === "string" ? value : "";
  }, [value, type, isDarkMode, colorValue]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied to clipboard",
      description: text,
      status: "success",
      duration: 2000,
      size: "sm",
    });
  };

  return (
    <Card size="sm" cursor="pointer" onClick={() => copyToClipboard(token)} _hover={{ bg: "gray.50" }}>
      <CardBody p={4}>
        <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.small}>
          {example && (
            <Box minH="40px" display="flex" alignItems="center" justifyContent="center">
              {example}
            </Box>
          )}
          <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.tiny}>
            <CDS.Text variant={CDS.Text.variant.labelMedium} color={CDS.Color.text.primary}>
              {name}
            </CDS.Text>
            <Code fontSize="xs" p={1}>
              {token}
            </Code>
            <CDS.Text variant={CDS.Text.variant.caption} color={CDS.Color.text.secondary}>
              {resolvedValue}
            </CDS.Text>
          </CDS.Stack>
        </CDS.Stack>
      </CardBody>
    </Card>
  );
}

function SpacingSection() {
  const spacingTokens: Array<{ name: string; token: SpacingToken; tokenString: string; description: string }> = [
    { name: "Zero", token: CDS.Spacing.zero, tokenString: "CDS.Spacing.zero", description: "No spacing (0px)" },
    {
      name: "Tiny",
      token: CDS.Spacing.tiny,
      tokenString: "CDS.Spacing.tiny",
      description: "Tight spacing for compact layouts",
    },
    {
      name: "Small",
      token: CDS.Spacing.small,
      tokenString: "CDS.Spacing.small",
      description: "Small gaps between related elements",
    },
    {
      name: "Medium",
      token: CDS.Spacing.medium,
      tokenString: "CDS.Spacing.medium",
      description: "Standard spacing for most use cases",
    },
    {
      name: "Large",
      token: CDS.Spacing.large,
      tokenString: "CDS.Spacing.large",
      description: "Generous spacing for breathing room",
    },
    {
      name: "Extra Large",
      token: CDS.Spacing.extraLarge,
      tokenString: "CDS.Spacing.extraLarge",
      description: "Large gaps between sections",
    },
    {
      name: "Huge",
      token: CDS.Spacing.huge,
      tokenString: "CDS.Spacing.huge",
      description: "Maximum spacing for major sections",
    },
  ];

  return (
    <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.large}>
      <Box>
        <CDS.Text variant={CDS.Text.variant.headlineSmall} color={CDS.Color.text.primary} mb={4}>
          Semantic Spacing
        </CDS.Text>
        <SimpleGrid columns={{ base: 2, md: 3, lg: 7 }} spacing={4}>
          {spacingTokens.map((spacing) => (
            <TokenDisplay
              key={spacing.tokenString}
              name={spacing.name}
              value={resolveSpacing(spacing.token)}
              token={spacing.tokenString}
              type="spacing"
              example={<Box bg="blue.500" h="20px" w={resolveSpacing(spacing.token)} borderRadius="2px" />}
            />
          ))}
        </SimpleGrid>
      </Box>
    </CDS.Stack>
  );
}

function ColorSection() {
  const textColors = [
    {
      name: "Primary",
      token: CDS.Color.text.primary,
      tokenString: "CDS.Color.text.primary",
      description: "Main text color",
    },
    {
      name: "Secondary",
      token: CDS.Color.text.secondary,
      tokenString: "CDS.Color.text.secondary",
      description: "Supporting text",
    },
    {
      name: "Tertiary",
      token: CDS.Color.text.tertiary,
      tokenString: "CDS.Color.text.tertiary",
      description: "Subtle text",
    },
    {
      name: "Quaternary",
      token: CDS.Color.text.quaternary,
      tokenString: "CDS.Color.text.quaternary",
      description: "Muted text",
    },
    {
      name: "Placeholder",
      token: CDS.Color.text.placeholder,
      tokenString: "CDS.Color.text.placeholder",
      description: "Input placeholder text",
    },
    { name: "Link", token: CDS.Color.text.link, tokenString: "CDS.Color.text.link", description: "Clickable links" },
    {
      name: "Success",
      token: CDS.Color.text.success,
      tokenString: "CDS.Color.text.success",
      description: "Success messages",
    },
    {
      name: "Warning",
      token: CDS.Color.text.warning,
      tokenString: "CDS.Color.text.warning",
      description: "Warning messages",
    },
    { name: "Error", token: CDS.Color.text.error, tokenString: "CDS.Color.text.error", description: "Error messages" },
  ];

  const surfaceColors = [
    {
      name: "Primary",
      token: CDS.Color.surface.primary,
      tokenString: "CDS.Color.surface.primary",
      description: "Main background",
    },
    {
      name: "Secondary",
      token: CDS.Color.surface.secondary,
      tokenString: "CDS.Color.surface.secondary",
      description: "Subtle background",
    },
    {
      name: "Tertiary",
      token: CDS.Color.surface.tertiary,
      tokenString: "CDS.Color.surface.tertiary",
      description: "Card backgrounds",
    },
    {
      name: "Elevated",
      token: CDS.Color.surface.elevated,
      tokenString: "CDS.Color.surface.elevated",
      description: "Elevated surfaces",
    },
    {
      name: "Overlay",
      token: CDS.Color.surface.overlay,
      tokenString: "CDS.Color.surface.overlay",
      description: "Modal overlays",
    },
    {
      name: "Input",
      token: CDS.Color.surface.input,
      tokenString: "CDS.Color.surface.input",
      description: "Form inputs",
    },
  ];

  const primitiveColors = [
    { name: "Brand", hue: "brand" as const, scales: [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950] },
    { name: "Gray", hue: "gray" as const, scales: [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950] },
    { name: "Green", hue: "green" as const, scales: [50, 100, 200, 300, 400, 500, 600, 700, 800, 900] },
    { name: "Red", hue: "red" as const, scales: [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950] },
    { name: "Orange", hue: "orange" as const, scales: [50, 100, 200, 300, 400, 500, 600, 700, 800, 900] },
    { name: "Blue", hue: "blue" as const, scales: [50, 100, 200, 300] },
    { name: "Indigo", hue: "indigo" as const, scales: [50, 100, 200, 300, 400, 500, 600, 700, 800, 900] },
  ];

  return (
    <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.extraLarge}>
      <Box>
        <CDS.Text variant={CDS.Text.variant.headlineSmall} color={CDS.Color.text.primary} mb={4}>
          Text Colors
        </CDS.Text>
        <SimpleGrid columns={{ base: 2, md: 4, lg: 8 }} spacing={4}>
          {textColors.map((color) => (
            <TokenDisplay
              key={color.tokenString}
              name={color.name}
              value={color.token}
              token={color.tokenString}
              type="color"
              example={
                <CDS.Text variant={CDS.Text.variant.labelMedium} color={color.token}>
                  Sample Text
                </CDS.Text>
              }
            />
          ))}
        </SimpleGrid>
      </Box>

      <Box>
        <CDS.Text variant={CDS.Text.variant.headlineSmall} color={CDS.Color.text.primary} mb={4}>
          Surface Colors
        </CDS.Text>
        <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={4}>
          {surfaceColors.map((color) => (
            <TokenDisplay
              key={color.tokenString}
              name={color.name}
              value={color.token}
              token={color.tokenString}
              type="color"
              example={<ReactiveColorBox colorToken={color.token} />}
            />
          ))}
        </SimpleGrid>
      </Box>

      <Box>
        <CDS.Text variant={CDS.Text.variant.headlineSmall} color={CDS.Color.text.primary} mb={4}>
          Primitive Colors
        </CDS.Text>
        <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.medium}>
          {primitiveColors.map((colorGroup) => (
            <Box key={colorGroup.hue}>
              <CDS.Text
                variant={CDS.Text.variant.labelMedium}
                color={CDS.Color.text.primary}
                mb={2}
                textTransform="capitalize"
              >
                {colorGroup.name}
              </CDS.Text>
              <CDS.Stack
                direction={CDS.Stack.direction.horizontal}
                spacing={CDS.Spacing.tiny}
                style={{ flexWrap: "wrap" }}
              >
                {colorGroup.scales.map((scale) => (
                  <ReactivePrimitiveColorBox
                    key={scale}
                    hue={colorGroup.hue}
                    scale={scale}
                    onCopy={() => {
                      navigator.clipboard.writeText(`CDS.Color.primitive.${colorGroup.hue}(${scale})`);
                    }}
                  />
                ))}
              </CDS.Stack>
            </Box>
          ))}
        </CDS.Stack>
      </Box>
    </CDS.Stack>
  );
}

function TypographySection() {
  const typographyVariants = [
    { name: "Display Large", variant: "display-large", sample: "The quick brown fox" },
    { name: "Display Medium", variant: "display-medium", sample: "The quick brown fox" },
    { name: "Display Small", variant: "display-small", sample: "The quick brown fox" },
    { name: "Headline Large", variant: "headline-large", sample: "The quick brown fox jumps" },
    { name: "Headline Medium", variant: "headline-medium", sample: "The quick brown fox jumps" },
    { name: "Headline Small", variant: "headline-small", sample: "The quick brown fox jumps" },
    { name: "Title Large", variant: "title-large", sample: "The quick brown fox jumps over" },
    { name: "Title Medium", variant: "title-medium", sample: "The quick brown fox jumps over" },
    { name: "Title Small", variant: "title-small", sample: "The quick brown fox jumps over" },
    { name: "Body Large", variant: "body-large", sample: "The quick brown fox jumps over the lazy dog" },
    { name: "Body Medium", variant: "body-medium", sample: "The quick brown fox jumps over the lazy dog" },
    { name: "Body Small", variant: "body-small", sample: "The quick brown fox jumps over the lazy dog" },
    { name: "Label Large", variant: "label-large", sample: "LABEL TEXT" },
    { name: "Label Medium", variant: "label-medium", sample: "LABEL TEXT" },
    { name: "Label Small", variant: "label-small", sample: "LABEL TEXT" },
    { name: "Caption", variant: "caption", sample: "Caption text for additional information" },
  ];

  return (
    <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.large}>
      <CDS.Text variant={CDS.Text.variant.headlineSmall} color={CDS.Color.text.primary}>
        Typography Scale
      </CDS.Text>
      <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.medium}>
        {typographyVariants.map((typo) => (
          <Card key={typo.variant} size="sm">
            <CardBody>
              <Grid templateColumns="200px 1fr 200px" gap={4} alignItems="center">
                <CDS.Stack align={CDS.Stack.align.start} spacing={CDS.Spacing.tiny}>
                  <CDS.Text variant={CDS.Text.variant.labelMedium} color={CDS.Color.text.primary}>
                    {typo.name}
                  </CDS.Text>
                  <Code fontSize="xs">
                    {'CDS.Text variant="'}
                    {typo.variant}
                    {'"'}
                  </Code>
                </CDS.Stack>
                <Box>
                  <CDS.Text
                    variant={(() => {
                      const camelCase = typo.variant.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
                      return (CDS.Text.variant as any)[camelCase] || CDS.Text.variant.bodyMedium;
                    })()}
                    color={CDS.Color.text.primary}
                  >
                    {typo.sample}
                  </CDS.Text>
                </Box>
                <Button
                  size="xs"
                  variant="outline"
                  onClick={() =>
                    navigator.clipboard.writeText(`<CDS.Text variant="${typo.variant}">${typo.sample}</CDS.Text>`)
                  }
                >
                  Copy
                </Button>
              </Grid>
            </CardBody>
          </Card>
        ))}
      </CDS.Stack>
    </CDS.Stack>
  );
}

function ShadowSection() {
  const shadowTokens = [
    // Elevation levels
    { name: "None", token: CDS.Shadow.none, tokenString: "CDS.Shadow.none", description: "No shadow" },
    { name: "Subtle", token: CDS.Shadow.subtle, tokenString: "CDS.Shadow.subtle", description: "Very light shadow" },
    { name: "Small", token: CDS.Shadow.small, tokenString: "CDS.Shadow.small", description: "Small elevation" },
    { name: "Medium", token: CDS.Shadow.medium, tokenString: "CDS.Shadow.medium", description: "Medium elevation" },
    { name: "Large", token: CDS.Shadow.large, tokenString: "CDS.Shadow.large", description: "Large elevation" },
    {
      name: "Extra Large",
      token: CDS.Shadow.extraLarge,
      tokenString: "CDS.Shadow.extraLarge",
      description: "Very high elevation",
    },
    { name: "Huge", token: CDS.Shadow.huge, tokenString: "CDS.Shadow.huge", description: "Maximum elevation" },
  ];

  const interactionShadows = [
    {
      name: "Focus",
      token: CDS.Shadow.focus,
      tokenString: "CDS.Shadow.focus",
      description: "Focus ring for accessibility",
    },
    { name: "Outline", token: CDS.Shadow.outline, tokenString: "CDS.Shadow.outline", description: "Subtle outline" },
    { name: "Inset", token: CDS.Shadow.inset, tokenString: "CDS.Shadow.inset", description: "Inward shadow" },
  ];

  const componentShadows = [
    {
      name: "Button",
      token: CDS.Shadow.button,
      tokenString: "CDS.Shadow.button",
      description: "Default button shadow",
    },
    {
      name: "Button Hover",
      token: CDS.Shadow.buttonHover,
      tokenString: "CDS.Shadow.buttonHover",
      description: "Button hover state",
    },
    { name: "Card", token: CDS.Shadow.card, tokenString: "CDS.Shadow.card", description: "Card elevation" },
    { name: "Modal", token: CDS.Shadow.modal, tokenString: "CDS.Shadow.modal", description: "Modal overlay shadow" },
    {
      name: "Dropdown",
      token: CDS.Shadow.dropdown,
      tokenString: "CDS.Shadow.dropdown",
      description: "Dropdown menu shadow",
    },
    { name: "Tooltip", token: CDS.Shadow.tooltip, tokenString: "CDS.Shadow.tooltip", description: "Tooltip shadow" },
  ];

  const stateShadows = [
    {
      name: "Success",
      token: CDS.Shadow.success,
      tokenString: "CDS.Shadow.success",
      description: "Success state shadow",
    },
    {
      name: "Warning",
      token: CDS.Shadow.warning,
      tokenString: "CDS.Shadow.warning",
      description: "Warning state shadow",
    },
    { name: "Error", token: CDS.Shadow.error, tokenString: "CDS.Shadow.error", description: "Error state shadow" },
  ];

  return (
    <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.extraLarge}>
      <Box>
        <CDS.Text variant={CDS.Text.variant.headlineSmall} color={CDS.Color.text.primary} mb={4}>
          Elevation Shadows
        </CDS.Text>
        <SimpleGrid columns={{ base: 2, md: 4, lg: 7 }} spacing={4}>
          {shadowTokens.map((shadow) => (
            <TokenDisplay
              key={shadow.tokenString}
              name={shadow.name}
              value={shadow.token}
              token={shadow.tokenString}
              type="shadow"
              example={<ReactiveShadowBox shadowToken={shadow.token} />}
            />
          ))}
        </SimpleGrid>
      </Box>

      <Box>
        <CDS.Text variant={CDS.Text.variant.headlineSmall} color={CDS.Color.text.primary} mb={4}>
          Interaction Shadows
        </CDS.Text>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
          {interactionShadows.map((shadow) => (
            <TokenDisplay
              key={shadow.tokenString}
              name={shadow.name}
              value={shadow.token}
              token={shadow.tokenString}
              type="shadow"
              example={<ReactiveShadowBox shadowToken={shadow.token} />}
            />
          ))}
        </SimpleGrid>
      </Box>

      <Box>
        <CDS.Text variant={CDS.Text.variant.headlineSmall} color={CDS.Color.text.primary} mb={4}>
          Component Shadows
        </CDS.Text>
        <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={4}>
          {componentShadows.map((shadow) => (
            <TokenDisplay
              key={shadow.tokenString}
              name={shadow.name}
              value={shadow.token}
              token={shadow.tokenString}
              type="shadow"
              example={<ReactiveShadowBox shadowToken={shadow.token} />}
            />
          ))}
        </SimpleGrid>
      </Box>

      <Box>
        <CDS.Text variant={CDS.Text.variant.headlineSmall} color={CDS.Color.text.primary} mb={4}>
          State Shadows
        </CDS.Text>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
          {stateShadows.map((shadow) => (
            <TokenDisplay
              key={shadow.tokenString}
              name={shadow.name}
              value={shadow.token}
              token={shadow.tokenString}
              type="shadow"
              example={<ReactiveShadowBox shadowToken={shadow.token} />}
            />
          ))}
        </SimpleGrid>
      </Box>
    </CDS.Stack>
  );
}

export function FoundationViewer() {
  const [activeTab, setActiveTab] = useState("spacing");

  const tabs = [
    { id: "spacing", name: "Spacing", component: SpacingSection },
    { id: "colors", name: "Colors", component: ColorSection },
    { id: "typography", name: "Typography", component: TypographySection },
    { id: "shadows", name: "Shadows", component: ShadowSection },
  ];

  const ActiveComponent = tabs.find((tab) => tab.id === activeTab)?.component;

  return (
    <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.large}>
      <CDS.Stack direction={CDS.Stack.direction.horizontal} spacing={CDS.Spacing.small}>
        {tabs.map((tab) => (
          <Button
            key={tab.id}
            size="sm"
            variant={activeTab === tab.id ? "solid" : "ghost"}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.name}
          </Button>
        ))}
      </CDS.Stack>

      <CDS.Divider color={CDS.Color.border.secondary} />

      <Box>{ActiveComponent && <ActiveComponent />}</Box>
    </CDS.Stack>
  );
}
